import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { Box, Icon } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  MilestonePackageRoute,
  MilestoneRouteToQueueTypeMap,
  QueueStatus,
  RequirementFlagSettingsType,
} from 'types';

import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';

import { Flag, FlagProps } from './Flag';
import { FlagInfo } from './FlagInfo';
import { QueueFlagsContext } from './QueueFlagsContext';

interface FlagBoxProps extends FlagProps {
  children?: React.ReactNode;
  displayWithNoQueue?: boolean;
  isLast?: boolean;
  flagType: RequirementFlagSettingsType;
}

interface CommonContentProps {
  children?: React.ReactNode;
  dataKey: string;
  hasFlags: boolean;
  width: string;
}

const TitleContent = ({ dataKey }: Pick<CommonContentProps, 'dataKey'>) => (
  <Box gap="2xs">
    <Box fontWeight="medium" fontSize="md">
      {dataKey}
    </Box>
  </Box>
);

const ChildrenContent = ({ children }: Pick<CommonContentProps, 'children'>) =>
  children ? (
    <Box direction={{ base: 'column', tablet: 'column' }} childGap="lg" width="100">
      {children}
    </Box>
  ) : null;

const FlagButton = ({
  dataKey,
  fileName,
  baseCategory,
  flagType,
  shouldHide,
  hasFlags,
}: FlagProps & { shouldHide: boolean; hasFlags: boolean }) =>
  !shouldHide ? (
    <Box alignItems="flex-end">
      <Flag {...{ dataKey, fileName, baseCategory, flagType, hasFlags }} />
    </Box>
  ) : null;

interface FlagContentProps {
  children?: React.ReactNode;
  dataKey: string;
  hasFlags: boolean;
  showFlagToEPC: boolean;
  shouldHide: boolean;
  fileName?: string;
  baseCategory?: string;
  flagType: RequirementFlagSettingsType;
  flagDrivenPackageReview: boolean;
}

const FlagContent = ({
  children,
  dataKey,
  hasFlags,
  showFlagToEPC,
  shouldHide,
  fileName,
  baseCategory,
  flagType,
  flagDrivenPackageReview,
}: FlagContentProps) => {
  if (flagDrivenPackageReview) {
    return (
      <>
        {hasFlags && showFlagToEPC && (
          <Box width="100">
            <FlagInfo {...{ dataKey, fileName, flagType, hasFlags }} />
          </Box>
        )}
        <Box
          width="100"
          direction={{ base: 'column', tablet: 'row' }}
          gap={{ base: 'lg', tablet: 'lg' }}
          alignItems={{ tablet: 'center' }}
          justifyContent="space-between"
        >
          <Box
            alignItems="flex-start"
            gap={{ base: 'lg', tablet: 'lg' }}
            direction={{ base: 'column', tablet: 'row' }}
          >
            <TitleContent dataKey={dataKey} />
          </Box>
          <ChildrenContent>{children}</ChildrenContent>
          <FlagButton {...{ dataKey, fileName, baseCategory, flagType, shouldHide, hasFlags }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <Box alignItems="flex-start" gap={{ base: 'lg', tablet: 'lg' }} direction={{ base: 'column', tablet: 'row' }}>
        {hasFlags && showFlagToEPC && (
          <Box>
            <Icon name="flag" size="lg" color="danger" />
          </Box>
        )}
        <Box gap="2xs">
          <TitleContent dataKey={dataKey} />
          <FlagInfo {...{ dataKey, fileName, flagType, hasFlags }} />
        </Box>
      </Box>
      <ChildrenContent>{children}</ChildrenContent>
      <FlagButton {...{ dataKey, fileName, baseCategory, flagType, shouldHide, hasFlags }} />
    </>
  );
};

export const FlagBox = ({
  children,
  dataKey,
  displayWithNoQueue = false,
  fileName,
  isLast = true,
  baseCategory,
  flagType,
}: FlagBoxProps) => {
  const { flagDrivenPackageReview } = useFlags();
  const { getFlag, getFlagsByFlagType, shouldHide, getVisibleFlags } = useContext(QueueFlagsContext);
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const milestonePackageURI = location.pathname.split('/')[3];

  const { data: queue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: MilestoneRouteToQueueTypeMap[milestonePackageURI as MilestonePackageRoute],
  });
  const currentFlag = getFlag(dataKey, fileName, false);
  const currentFlags = getFlagsByFlagType(flagType, false, dataKey, fileName) || [];
  const allFlags = [...(currentFlags ? currentFlags : []), ...(currentFlag ? [currentFlag] : [])];
  const visibleFlags = getVisibleFlags(allFlags);
  const hasFlags = flagDrivenPackageReview ? Boolean(visibleFlags?.length) : Boolean(currentFlag);
  const showFlagToEPC = flagDrivenPackageReview ? !(queue?.status === QueueStatus.submitted && !isFinCoUser) : true;

  return (hasFlags && showFlagToEPC) || !shouldHide || displayWithNoQueue ? (
    <Box
      background={hasFlags && showFlagToEPC ? 'danger' : 'primary'}
      direction={flagDrivenPackageReview ? 'column' : { base: 'column', tablet: 'row' }}
      alignItems={{ tablet: 'center' }}
      gap={{ base: 'lg', tablet: 'lg' }}
      radius="md"
      padding="lg"
      borderColor={hasFlags && flagDrivenPackageReview && showFlagToEPC ? 'danger-500' : 'separator'}
      borderWidth={hasFlags && flagDrivenPackageReview && showFlagToEPC ? '0 0 0 md' : 'xs'}
      margin={isLast ? '0' : '0 0 lg 0'}
      {...(!flagDrivenPackageReview && { justifyContent: 'space-between' })}
    >
      <FlagContent
        dataKey={dataKey}
        hasFlags={hasFlags && showFlagToEPC}
        shouldHide={shouldHide}
        fileName={fileName}
        baseCategory={baseCategory}
        flagType={flagType}
        flagDrivenPackageReview={flagDrivenPackageReview}
        showFlagToEPC={showFlagToEPC}
      >
        {children}
      </FlagContent>
    </Box>
  ) : null;
};
